import { lazy } from 'react';
import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerContext } from '#/horizon/server-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { getListingPublishedDetails } from '#/modules/listing/helpers/getListingPublishedDetails';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import { listingApiIsomorphicFetchInternship } from '#/modules/listing/internships/api/listingApiFetchInternship';
import { viewInternshipReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { ClientOnlyLazySearchInternshipRefinements } from '#/modules/search/internships/components/Refinements/ClientOnlyLazySearchInternshipRefinements';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewInternshipPage } from './ViewInternshipPage';

const LazyExpiredInternshipPage = lazy(() =>
  import('#/modules/listing/internships/pages/expired/ExpiredInternshipPage').then(
    (m) => ({ default: m.ExpiredInternshipPage }),
  ),
);

const LazyUnpublishedInternshipPage = lazy(() =>
  import('#/modules/listing/internships/pages/unpublished/UnpublishedInternshipPage').then(
    (m) => ({ default: m.UnpublishedInternshipPage }),
  ),
);

export const handle = {
  defaultSearchType: 'INTERNSHIP',
  showDesktopSearch: true,
  showSearchRefinements: true,
  searchRefinements: <ClientOnlyLazySearchInternshipRefinements />,
  hideFooterDivider: true,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  context,
  params: { internshipIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewInternshipReferenceRoute>) {
  const { serverContext } = getServerContext(context);

  const [internshipId] = internshipIdDashSlug.split('-');

  try {
    const [internship, listingExtras] = await Promise.all([
      listingApiIsomorphicFetchInternship({ request, context })(internshipId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiIsomorphicFetchListingExtras({
            request,
            context,
          })([internshipId])
        : null,
    ]);

    const { saved, applicationId } = listingExtras?.[internshipId] ?? {
      saved: false,
      applicationId: null,
    };

    switch (internship.type) {
      case 'INTERNSHIP': {
        const expired = isListingExpired(internship.expires);
        return {
          type: expired ? 'EXPIRED_INTERNSHIP' : internship.type,
          internship,
          publishedDetails: getListingPublishedDetails({
            publishedDate: internship.firstPublished,
            status: expired
              ? ListingViewStatus.EXPIRED
              : ListingViewStatus.PUBLISHED,
            context,
          }),
          locale: serverContext.locale,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: internship.url,
          } satisfies LayoutHandleProps,
          applicationId,
          saved,
        } as const;
      }
      case 'UNPUBLISHED_INTERNSHIP':
        return idealistNotFound({
          type: internship.type,
          unpublishedInternship: internship,
        } as const);
    }
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewInternshipHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'INTERNSHIP':
      return (
        <RouteModule
          title={`${loaderData.internship.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['INTERNSHIP']}`}
          ogImage={
            loaderData.internship.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data:
                    loaderData.internship.orgLogoOverride ??
                    loaderData.internship.org?.logo,
                  appConfig,
                })
          }
          description={loaderData.internship.descriptionTruncated.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.internship.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.internship.url[loaderData.internship.locale],
          }}
          schemaMarkups={[
            loaderData.internship.schemaInternshipMarkup,
            loaderData.internship.schemaBreadcrumbListMarkup?.[
              loaderData.locale
            ],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.internship}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewInternshipPage
                internship={loaderData.internship}
                publishedDetails={loaderData.publishedDetails}
                listingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                listingApplicationFailure={listingApplicationFailure}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    case 'EXPIRED_INTERNSHIP':
      return (
        <RouteModule title={loaderData.internship.name}>
          <LazyExpiredInternshipPage
            internship={loaderData.internship}
            listingApplicationId={loaderData.applicationId}
            publishedDetails={loaderData.publishedDetails}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_INTERNSHIP':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <LazyUnpublishedInternshipPage
            unpublishedInternship={loaderData.unpublishedInternship}
          />
        </RouteModule>
      );
  }
}
